import { Formik } from 'formik'
import { useDispatch, useSelector } from '../../store'
import {
  type AccountDataSelection as AccountData,
  LoadingTypes,
  isLoading,
} from '@labrav/common'
import { CloudType } from '../../types'
import { FooterV2 } from '../../common/components/FooterV2/FooterV2'
import { ButtonV2 } from '@labrav/react-components'
import { useStyles } from './AzureCombinedForm.styles'
import { SelectChangeEvent } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { getFlyoutListingDataMicrosoft } from '../../modules/flyOutSyncs/action'
import { AzureCombinedFormSections } from './AzureCombinedFormSections/AzureCombinedFormSections'
import { MsftCombinedFormState } from '../../modules/microsoftCombinedForm/reducer'
import {
  getSelectedAccountDetail,
  submitCombinedFormData,
} from '../../modules/microsoftCombinedForm/action'
import { jwtDecode } from 'jwt-decode'
import { CombinedFormProductSelection } from './CombinedFormProductSelection/CombinedFormProductSelection'
import { CombinedFormCloudAccountSelector } from '../CombinedFormCloudAccountSelector/CombinedFormCloudAccountSelector'
import azureCombinedFormValidationSchema from '../../common/utils/validateAzureCombinedForm'

const AzureCombinedForm = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { labraFormId, secureKey } = useParams<{
    labraFormId: string
    secureKey: string
  }>()
  const queryParams = new URLSearchParams(location.search)
  const viewingPersona = queryParams.get('viewing_persona') as string
  const labraIsvAccountId = queryParams.get('labra_isv_id') as string
  const labraProductID = queryParams.get('labra_product_id') as string
  const formDisabled = viewingPersona === 'ISV'
  const isProductFromUrl = Boolean(labraProductID)

  const submitCombinedFormLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.SUBMIT_COMBINED_FORM)
  )

  const combinedFormData = useSelector<MsftCombinedFormState>(
    state => state.microsoftCombinedForm
  )
  useEffect(() => {
    if (labraIsvAccountId) {
      dispatch(
        getFlyoutListingDataMicrosoft({
          accountId: labraIsvAccountId,
        })
      )
    }
  }, [labraIsvAccountId])

  const accountData = useSelector<AccountData[]>(state =>
    ((state.accountSelection?.availableAccounts || []) as AccountData[]).filter(
      account =>
        account?.cloud === 'AZURE' && account?.product === ('FLYOUT' as string)
    )
  )

  const handleSuccess = () => {
    if (typeof document !== 'undefined' && document.referrer) {
      try {
        const targetOrigin = new URL(document.referrer).origin
        window.parent.postMessage(
          {
            type: 'LABRA_FORM_SAVED',
            payload: {
              labra_reseller_id: combinedFormData?.labraResellerId,
              labra_isv_id: labraIsvAccountId,
              combined_form_id: labraFormId,
              external_id: combinedFormData?.externalId,
              labra_product_id: labraProductID,
            },
          },
          targetOrigin
        )
      } catch {
        console.error('Invalid document.referrer URL:', document.referrer)
      }
    } else {
      console.error(
        'Cannot determine parent origin - referrer is not available.'
      )
    }
  }

  const handleSubmit = ({
    combinedFormValues,
  }: {
    combinedFormValues: MsftCombinedFormState
  }) => {
    const secureKeyValue = window.LABRA_JWT || secureKey
    if (labraFormId && secureKeyValue) {
      const customClaims = jwtDecode<{
        custom_claims: Record<string, unknown>
      }>(secureKeyValue)?.custom_claims
      dispatch(
        submitCombinedFormData({
          combinedFormData: combinedFormValues,
          formId: labraFormId,
          isvCloudAccountId: labraIsvAccountId,
          externalMetadata: customClaims as Record<string, unknown>,
          handleSuccess: handleSuccess,
        })
      )
    }
  }

  return (
    <div className={classes.root}>
      <Formik
        initialValues={combinedFormData}
        validationSchema={azureCombinedFormValidationSchema}
        validateOnMount={true}
        onSubmit={values => handleSubmit({ combinedFormValues: values })}
        enableReinitialize
      >
        {formikProps => {
          useEffect(() => {
            if (!labraProductID) return
            if (!formikProps.values.draftOffer?.labraProductId) {
              formikProps.setFieldValue(
                'draftOffer.labraProductId',
                labraProductID
              )
            }
          }, [labraProductID])
          const handleChangeSelect = async (e: SelectChangeEvent) => {
            formikProps.setValues({
              ...formikProps.values,
              labraResellerId: e.target.value,
            })
            const mpnId = await dispatch(
              getSelectedAccountDetail({ accountId: e.target.value })
            )
            if (mpnId) {
              formikProps.setFieldValue('draftOffer.resellerDetails', [
                {
                  id: mpnId,
                  partnerName: '',
                },
              ])
            }
          }
          const handleSaveClick = () => {
            formikProps.submitForm()
          }
          return (
            <>
              <CombinedFormCloudAccountSelector
                cloud={CloudType.AZURE}
                accounts={accountData}
                value={formikProps.values.labraResellerId}
                keyName="labraCpId"
                handleChangeSelect={handleChangeSelect}
                error={
                  formikProps.touched.labraResellerId &&
                  Boolean(formikProps.errors.labraResellerId)
                }
                errorMessage={
                  formikProps.touched.labraResellerId
                    ? (formikProps.errors.labraResellerId as string)
                    : undefined
                }
              />
              <CombinedFormProductSelection
                disableProductSelection={isProductFromUrl}
                disabled={formDisabled || isProductFromUrl}
              />
              {formikProps.values.draftOffer?.labraProductId && (
                <AzureCombinedFormSections />
              )}

              <FooterV2
                actions={
                  <div className={classes.footerWrapper}>
                    <div className={classes.footerWrapperButtons}>
                      <ButtonV2
                        styleType="primary"
                        onClick={handleSaveClick}
                        data-testid={'save-as-draft'}
                        loading={submitCombinedFormLoading}
                      >
                        {'Save as draft'}
                      </ButtonV2>
                    </div>
                  </div>
                }
                width={'-webkit-fill-available'}
                customClass={classes.customFooter}
              />
            </>
          )
        }}
      </Formik>
    </div>
  )
}

export default AzureCombinedForm
